import * as Yup from 'yup';
import { hasDollarInString } from '../../../utils/utils';

export const generateInitialFormValues = (fieldGrid, studentId, customerNumber) => {
    const values = {};

    for (const rowFields of fieldGrid) {
        for (const field of rowFields) {
            const { input_name: name, is_active, input_value } = field || {};

            if (!name) continue;

            if (name === 'stud_bd_student_uuid' && studentId && !hasDollarInString(studentId)) {
                values[name] = studentId;
            } else if (name === 'customer_mobile' && customerNumber && !hasDollarInString(customerNumber)) {
                values[name] = customerNumber;
            } else if (is_active) {
                values[name] = input_value?.value || '';
            }
        }
    }

    return values;
};


export const generateValidationSchema = (fieldGrid, primaryIdentifierConfig = {}) => {
    const validationRules = {};
    const { primaryIdentifier, isPrimaryIdentifierFlow } = primaryIdentifierConfig;

    for (const rowFields of fieldGrid) {
        for (const field of rowFields) {
            const fieldName = field?.input_name;
            if (!fieldName) continue;

            validationRules[fieldName] = getYupRule(field, primaryIdentifier, isPrimaryIdentifierFlow);
        }
    }

    return Yup.object().shape(validationRules);
};

const getYupRule = (field, primaryIdentifier, isPrimaryIdentifierFlow) => {
    if (!field?.is_active) return null;

    const { input_type, input_name, is_required } = field;
    const baseString = Yup.string().trim();

    // Handle primary identifier validation
    if (isPrimaryIdentifierFlow && input_name === 'stud_bd_student_uuid') {
        if (primaryIdentifier === 'customer_mobile') {
            // Validate as mobile number
            const mobileValidation = baseString
                .length(10, 'Mobile number must contain 10 digits')
                .matches(/^[6-9][0-9]{9}$/, 'Please enter valid mobile number');

            return is_required
                ? mobileValidation.required('Please enter mobile number')
                : mobileValidation;
        } else if (primaryIdentifier === 'customer_email') {
            // Validate as email
            return is_required
                ? baseString.required('Please enter email address').email('Please enter valid email')
                : baseString.email('Please enter valid email');
        }
    }

    // Skip validation for customer fields when they are used as primary identifier
    if (isPrimaryIdentifierFlow) {
        if (primaryIdentifier === 'customer_mobile' && input_name === 'customer_mobile') {
            // Skip customer_mobile validation since input is captured in stud_bd_student_uuid
            return null;
        }
        if (primaryIdentifier === 'customer_email' && input_name === 'customer_email') {
            // Skip customer_email validation since input is captured in stud_bd_student_uuid
            return null;
        }
    }

    switch (input_type) {
        case 'text': {
            if (input_name === 'customer_mobile') {
                const mobileValidation = baseString
                    .length(10, 'Mobile number must contain 10 digits')
                    .matches(/^[6-9][0-9]{9}$/, 'Please enter valid mobile number');

                return is_required
                    ? mobileValidation.required('Please enter mobile number')
                    : mobileValidation;
            }

            return is_required
                ? baseString.required('Field cannot be empty')
                : baseString;
        }

        case 'email':
            return is_required
                ? baseString.required('Field cannot be empty').email('Please enter valid email')
                : baseString.email('Please enter valid email');

        case 'radio':
        case 'select':
        case 'date':
            return is_required
                ? baseString.required(getRequiredMessage(input_type))
                : baseString;

        case 'checkbox': {
            const arrayRule = Yup.array();
            return is_required
                ? arrayRule.required('Please select at least one option')
                : arrayRule;
        }

        default:
            return baseString;
    }
};

const getRequiredMessage = (type) => {
    const messages = {
        radio: 'Please select an option',
        select: 'Please select an option',
        date: 'Please select a date',
    };
    return messages[type] || 'Field cannot be empty';
};

const transformData = (data, prefix) => {
    try {
        if (!data || !prefix) return null;

        return Object.fromEntries(Object.entries(data).map(([key, value]) => [`${prefix}_${key}`, value]));
    } catch (error) {
        console.error('Error in transformData:', error);
    }
};

export const prefillCustomForm = (data, formik, formBlockData) => {
    if (!data || !formik || !formBlockData) return;

    const enableEmailOtpVerification = formBlockData?.form_config?.enable_email_otp_verification ?? false;

    // Destructure and clean unnecessary fields
    const { sbd_code, student_uuid, ...studBdRest } = data?.stud_bd || {};
    const { profile_pic_url, ...custBdRest } = data?.cust_bd || {};

    const combinedPrefillData = {
        ...transformData(studBdRest, 'stud_bd'),
        ...transformData(custBdRest, 'cust_bd'),
        ...transformData(data?.stud_ad, 'stud_ad'),
        ...transformData(data?.cust_ad, 'cust_ad'),
        ...data?.custom_fields,
    };

    // Filter matching fields from formik.values
    const updatedValues = Object.entries(formik.values).reduce((acc, [key, currentVal]) => {
        if (combinedPrefillData?.hasOwnProperty(key) && combinedPrefillData?.[key]) {
            acc[key] = combinedPrefillData[key];
        }
        return acc;
    }, {});


    // Handle checkbox conversion from CSV to array
    formBlockData?.fields?.forEach((row) => {
        row?.forEach((field) => {
            const fieldName = field?.input_name;
            if (field?.input_type === 'checkbox' && typeof updatedValues[fieldName] === 'string') {
                updatedValues[fieldName] = updatedValues[fieldName].split(',');
            }
        });
    });

    // Set values in Formik
    formik.setValues({
        ...formik.values,
        ...updatedValues,
        ...((custBdRest?.email && !enableEmailOtpVerification) && { customer_email: custBdRest?.email }),
    });
};

export const generatePayload = async (values, formBlockData) => {
    const system_fields = {};
    const custom_fields = [];

    formBlockData?.fields?.forEach((row) => {
        row?.forEach((field) => {
            if (!field?.is_active) return;

            const value = values[field.input_name];
            const isCheckbox = field.input_type === 'checkbox';
            const processedValue = isCheckbox && value !== "" ? value?.join(',') || "" : value;

            if (field.field_type === 'system' && processedValue) {
                system_fields[field.input_name] = processedValue;
            } else if (field.field_type === 'custom') {
                custom_fields.push({
                    code: field.field_code,
                    value: processedValue,
                });
            }
        });
    });

    return {
        system_fields,
        custom_fields,
    };
};

